import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { API_URL } from "../data/content";
import { useLang } from "../i18n";

const inputCls =
  "w-full rounded-2xl border border-bone bg-cream/60 px-5 py-3.5 text-sm text-charcoal placeholder:text-bark/50 focus:outline-none focus:ring-2 focus:ring-tree/50 focus:border-tree transition-all duration-300";

const fmtSlot = (time) => {
  const [h, m] = time.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

export const TourForm = ({ compact = false }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ parent_name: "", email: "", phone: "", child_age: "", preferred_date: "", time_slot: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState(null);
  const [success, setSuccess] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!form.preferred_date) { setSlots(null); return undefined; }
    setSlots("loading");
    setForm((f) => ({ ...f, time_slot: "" }));
    let cancelled = false;
    axios.get(`${API_URL}/tour-slots`, { params: { date: form.preferred_date } })
      .then((r) => { if (!cancelled) setSlots(r.data); })
      .catch(() => { if (!cancelled) setSlots(null); });
    return () => { cancelled = true; };
  }, [form.preferred_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.preferred_date && !form.time_slot) {
      toast.error(t("toast_err"), { description: t("form_time") });
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.preferred_date) delete payload.time_slot;
      const { data } = await axios.post(`${API_URL}/tour-requests`, payload);
      toast.success(t("toast_ok"), {
        description: t("toast_ok_desc")(form.parent_name.split(" ")[0], form.phone),
      });
      setSuccess({ link: data.calendar_link, date: form.preferred_date, slot: form.time_slot });
      setForm({ parent_name: "", email: "", phone: "", child_age: "", preferred_date: "", time_slot: "", message: "" });
      setSlots(null);
    } catch (err) {
      toast.error(t("toast_err"), { description: t("toast_err_desc") });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="tour-form-success" className="text-center py-6 space-y-4">
        <CheckCircle2 className="w-14 h-14 mx-auto text-tree" />
        <h3 className="font-display font-extrabold text-2xl text-forest">{t("form_success_title")}</h3>
        {success.date && success.slot && (
          <p className="text-forest text-sm font-bold">
            {new Date(success.date + "T12:00:00").toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { weekday: "long", month: "long", day: "numeric" })}
            {" · "}{fmtSlot(success.slot)}
          </p>
        )}
        <p className="text-sm text-bark max-w-xs mx-auto">{t("form_success_sub")}</p>
        <div>
          <button
            data-testid="tour-form-book-another"
            onClick={() => setSuccess(null)}
            className="text-sm font-bold text-tree hover:text-tangerine underline underline-offset-4 transition-colors"
          >
            {t("form_book_another")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="tour-booking-form" onSubmit={submit} className={compact ? "space-y-4" : "space-y-5"}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input required data-testid="tour-form-name" placeholder={t("form_name")} value={form.parent_name} onChange={set("parent_name")} className={inputCls} />
        <input required type="tel" data-testid="tour-form-phone" placeholder={t("form_phone")} value={form.phone} onChange={set("phone")} className={inputCls} />
      </div>
      <input required type="email" data-testid="tour-form-email" placeholder={t("form_email")} value={form.email} onChange={set("email")} className={inputCls} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <select required data-testid="tour-form-child-age" value={form.child_age} onChange={set("child_age")} className={inputCls}>
          <option value="" disabled>{t("form_age")}</option>
          {t("form_age_opts").map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input type="date" data-testid="tour-form-date" min={new Date().toISOString().split("T")[0]} value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} aria-label="Preferred tour date" />
      </div>

      {slots === "loading" && <p className="text-xs text-bark/70">{t("blog_loading")}</p>}
      {slots && slots !== "loading" && (
        slots.weekend ? (
          <p data-testid="tour-form-weekend-note" className="text-xs font-semibold text-tangerine">{t("form_weekend")}</p>
        ) : (
          <div data-testid="tour-form-slots">
            <p className="text-xs font-bold text-forest mb-2">{t("form_time")}</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {slots.slots.map((s) => (
                <button
                  type="button"
                  key={s.time}
                  data-testid={`tour-slot-${s.time}`}
                  disabled={!s.available}
                  onClick={() => setForm({ ...form, time_slot: s.time })}
                  className={`rounded-xl py-2 text-xs font-bold transition-all duration-200 ${
                    !s.available
                      ? "bg-bone/60 text-bark/40 line-through cursor-not-allowed"
                      : form.time_slot === s.time
                        ? "bg-tree text-white shadow"
                        : "bg-sage text-forest hover:bg-tree hover:text-white"
                  }`}
                >
                  {fmtSlot(s.time)}
                </button>
              ))}
            </div>
          </div>
        )
      )}

      <textarea rows={compact ? 3 : 4} data-testid="tour-form-message" placeholder={t("form_message")} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
      <button
        type="submit"
        disabled={loading}
        data-testid="tour-form-submit-btn"
        className="group w-full rounded-full bg-tangerine text-white py-4 font-display font-bold text-base flex items-center justify-center gap-2.5 hover:bg-clay transition-colors duration-300 disabled:opacity-60 shadow-xl shadow-tangerine/30"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CalendarCheck className="w-5 h-5 group-hover:rotate-6 transition-transform" />}
        {loading ? t("form_sending") : t("form_submit")}
      </button>
      <p className="text-xs text-bark/70 text-center">{t("form_note")}</p>
    </form>
  );
};
